import { lazy, Suspense } from "react";
import TopBar from "@/components/TopBar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import SEOHead from "@/components/SEOHead";
import { useFirstVisitIntro } from "@/hooks/useFirstVisitIntro";

// Lazy load below-the-fold sections to reduce initial JS bundle
const EmotionalFeatureSection = lazy(() => import("@/components/EmotionalFeatureSection"));
const ProductDetailsSection = lazy(() => import("@/components/ProductDetailsSection"));
const HowToOrderSection = lazy(() => import("@/components/HowToOrderSection"));
const ReviewsSection = lazy(() => import("@/components/ReviewsSection"));
const CTASection = lazy(() => import("@/components/CTASection"));
const CartDrawer = lazy(() => import("@/components/CartDrawer"));

const Index = () => {
  const { showIntro, prefersReducedMotion } = useFirstVisitIntro();

  // Organization structured data
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FunzyFeets",
    "url": "https://funzyfeets.shop",
    "logo": "https://funzyfeets.shop/favicon.png",
    "description": "FunzyFeets offers trendy, ultra-comfortable indoor slippers designed for everyday home comfort. Soft plush interiors, non-slip soles, and styles for every taste.",
    "sameAs": [
      "https://www.instagram.com/funzyfeets",
      "https://www.facebook.com/funzyfeets",
      "https://www.tiktok.com/@funzyfeets"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "support@funzyfeets.shop",
      "availableLanguage": ["English"]
    }
  };

  // WebSite structured data with search action
  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "FunzyFeets",
    "url": "https://funzyfeets.shop",
    "description": "Shop trendy indoor slippers with soft plush interiors and non-slip soles. From cute designs to minimalist styles — everyday comfort starts here.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://funzyfeets.shop/models?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "FunzyFeets",
      "logo": {
        "@type": "ImageObject",
        "url": "https://funzyfeets.shop/favicon.png"
      }
    }
  };

  // LocalBusiness structured data
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "Store",
    "name": "FunzyFeets",
    "image": "https://funzyfeets.shop/favicon.png",
    "url": "https://funzyfeets.shop",
    "description": "Online store for trendy and cozy indoor slippers. Soft plush interiors, lightweight design, non-slip soles, and styles for every taste. Worldwide delivery.",
    "priceRange": "€€",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2847",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${showIntro && !prefersReducedMotion ? 'intro-bg' : ''}`}>
      <SEOHead 
        title="FunzyFeets | Trendy Indoor Slippers for Everyday Comfort"
        description="Discover trendy indoor slippers designed for daily comfort. Soft plush interiors, lightweight design, and non-slip soles. From cute animal designs to cozy holiday styles — everyday comfort starts here."
        keywords="trendy slippers, cozy indoor slippers, comfortable house slippers, soft plush slippers, non-slip home slippers, stylish indoor footwear, everyday comfort slippers, cute home slippers, lightweight slippers, warm house shoes"
        canonicalUrl="https://funzyfeets.shop"
        ogImage="https://funzyfeets.shop/og-image.jpg"
        ogType="website"
      />
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessJsonLd) }}
      />
      <TopBar showIntro={showIntro && !prefersReducedMotion} />
      <Header showIntro={showIntro && !prefersReducedMotion} />
      <Suspense fallback={null}>
        <CartDrawer />
      </Suspense>
      <main className="flex-1">
        <HeroSection showIntro={showIntro && !prefersReducedMotion} />
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <EmotionalFeatureSection />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <ProductDetailsSection />
        </Suspense>
        <Suspense fallback={<div className="min-h-[300px]" />}>
          <HowToOrderSection />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <ReviewsSection />
        </Suspense>
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <CTASection />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
